import { useLocation } from "react-router-dom";
import { useSidebarLabel } from "../contexts/SidebarLabelsContext";

/**
 * PageHeader — big H1 + optional subtitle + action button. Auto-picks
 * up the agency's sidebar/H1 rename map (keyed by pathname), so an
 * admin renaming "Clients" → "Customers" in Settings → Sidebar Labels
 * changes the H1 too. Callers can force a specific override key via
 * `titleKey` when the pathname doesn't match the sidebar entry
 * (e.g., dynamic routes like `/clients/:id/portal-config`).
 */
export function PageHeader({ title, subtitle, action, testId, titleKey }) {
  const { pathname } = useLocation();
  const effectiveTitle = useSidebarLabel(titleKey || pathname, title);
  return (
    <div
      data-testid={testId || "page-header"}
      className="border-b border-[#27272A] px-4 sm:px-8 py-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4 bg-[#0A0A0A] sticky top-14 md:top-0 z-30"
    >
      <div>
        <div className="label-mute">Iris Security / Module</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter leading-none mt-2">
          {effectiveTitle}
        </h1>
        {subtitle && <p className="text-zinc-400 text-sm mt-2 max-w-2xl tracking-wide">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function Stat({ label, value, suffix, accent }) {
  return (
    <div className="border border-[#27272A] p-5 bg-[#121212] hover:border-[#3F3F46] transition-colors duration-150">
      <div className="label-mute">{label}</div>
      <div className="flex items-baseline gap-2 mt-3">
        <span
          className="font-mono font-black text-4xl tracking-tighter"
          style={accent ? { color: accent } : undefined}
        >
          {value}
        </span>
        {suffix && <span className="text-zinc-500 text-xs uppercase tracking-widest">{suffix}</span>}
      </div>
    </div>
  );
}

export function PillBadge({ children, tone = "default", ...rest }) {
  const tones = {
    default: "border-[#3F3F46] text-zinc-300",
    green: "border-[#34C759]/40 text-[#34C759] bg-[#34C759]/10",
    amber: "border-[#FF9F0A]/40 text-[#FF9F0A] bg-[#FF9F0A]/10",
    red: "border-[#FF3B30]/40 text-[#FF3B30] bg-[#FF3B30]/10",
    blue: "border-[#0A84FF]/40 text-[#0A84FF] bg-[#0A84FF]/10",
  };
  return (
    <span
      {...rest}
      className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-widest border ${tones[tone]} ${rest.className || ""}`}
    >
      {children}
    </span>
  );
}

export function ActionButton({ children, testid, ...rest }) {
  return (
    <button
      {...rest}
      data-testid={testid || rest["data-testid"]}
      className={`inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#FF453A] text-white px-4 py-2 text-xs uppercase tracking-widest font-display font-bold transition-colors duration-150 disabled:opacity-50 ${rest.className || ""}`}
    >
      {children}
    </button>
  );
}

export function GhostButton({ children, testid, ...rest }) {
  return (
    <button
      {...rest}
      data-testid={testid || rest["data-testid"]}
      className={`inline-flex items-center gap-2 border border-[#27272A] hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-300 px-3 py-1.5 text-xs uppercase tracking-widest transition-colors duration-150 ${rest.className || ""}`}
    >
      {children}
    </button>
  );
}

export function FormField({ label, children }) {
  return (
    <label className="block">
      <span className="label-mute block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

export const inputClass =
  "w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#FF3B30] outline-none px-3 py-2 text-sm transition-colors";
